import Backbone from 'backbone'
import _ from 'lodash'
import mousify from './mousify'
import $ from 'jquery'
import Snapping from './snap'
import leftClick from '../left-click'
import AutoScroll from './auto-scroll'
import Wobs from '../wobs'
import closestUntil from './closest-until'
import validDrag from './valid-drag'

var Draggable = Backbone.View.extend({

  events: {
    'touchstart .selected': 'listen',
    'mousedown .selected': 'listen'
  },

  initialize: function (options) {
    _.bindAll(this, 'drag', 'drop', 'onScroll')
    this.canvas = options.canvas
    this.scroller = new AutoScroll(options.scroll, options.canvas, this.onScroll)
  },

  onScroll: function () {
    // Fake a drag event so the wobs move
    this.drag({
      pageX: this.scroller._mouseCoords.x,
      pageY: this.scroller._mouseCoords.y
    })
  },

  listen: _.wrap(function (e) {
    this.controlWob = this.canvas.views[$(e.currentTarget).data('id')]

    if (this.controlWob.readonly || this.controlWob.model.get('locked')) {
      // Can't move readonly or locked wobs
      return
    }

    // Set a class on the canvas so other components know we're dragging wobs
    this.canvas.$el.addClass('is-dragging')

    // If the element we clicked on (or an ancestor within the wob) has the
    // 'no-drag' class, abort the drag logic
    if (closestUntil(e.target, '.no-drag', '.wob').length > 0) {
      return
    }

    e.preventDefault()

    let event = mousify(e)
      , area = this.canvas.selection.area(Wobs.filters.unlocked) // Only concerned with wobs that will be moved
      , coords = this.canvas.coords(event)

    // Store the original offset of this click from the actual wob coordinates
    this.clickOffset = {
      left: coords.left - area.left,
      top: coords.top - area.top
    }

    this.active = false
    this.snap = new Snapping({canvas: this.canvas, el: this.canvas.el})

    this._snapTimeout = setTimeout(() => {
      // Trigger snap code if they mousedown and hold the mouse. But ignore what it returns. We just want to activate lines
      this.snap.snap(area, {left: 0, top: 0})
    }, 300)

    this.canvas.disableWobEvents()

    this.scroller.start()
    this.scroller.mouse({
      x: event.pageX,
      y: event.pageY
    })

    // initial click details
    this._initialClick = {
      ms: performance.now(), // ms click started
      x: e.pageX, // Coords
      y: e.pageY
    }

    $(document).on('mousemove touchmove gesturechange', this.drag)
    $(document).on('mouseup touchend gestureend', this.drop)
  }, leftClick),

  _move: function (event) {
    let area = this.canvas.selection.area(Wobs.filters.unlocked)
      , coords = this.canvas.coords(event)
      , difference = {
        left: coords.left - this.clickOffset.left - area.left,
        top: coords.top - this.clickOffset.top - area.top
      }
      , virtualArea = _.extend({}, area, {
        // Set its coords to whatever it should be based on the diff
        left: area.left + difference.left,
        top: area.top + difference.top
      })

    let snapDiff = this.snap.snap(virtualArea, area)

    difference.left += (snapDiff.left || 0)
    difference.top += (snapDiff.top || 0)

    this.canvas.selection.moveBy(difference)

    return difference
  },

  drag: function (e) {
    if (!validDrag(this._initialClick, {x: e.pageX, y: e.pageY})) {
      // Don't start dragging yet
      return
    }

    delete this._initialClick // We're dragging, remove initialClick details

    clearTimeout(this._snapTimeout)

    var event = mousify(e)
      , self = this

    this.scroller.mouse(_.extend({}, {x: event.pageX, y: event.pageY}))

    if (this.active === false) {
      this.canvas.history.record()
      this.$el.trigger('start:dragging')
    }

    this.active = true

    var diff = this._move(event)

    // If we changed x position and we didn't autoscroll, resize the canvas
    if (diff.left !== 0 && !this.scroller.x) {
      this.canvas.resizeX()
    }
    // See above, but for y
    if (diff.top !== 0 && !this.scroller.y) {
      this.canvas.resizeY()
    }

    this.canvas.selection.wobs.forEach(function (wob) {
      self.canvas.collection.adjustZIndex(wob)
    })

    return this.$el.trigger('move:dragging', this.controlWob)
  },

  drop: function (e) {
    clearTimeout(this._snapTimeout)

    this.scroller.stop()
    this.canvas.enableWobEvents()

    $(document).off('mousemove touchmove gesturechange', this.drag)
    $(document).off('mouseup touchend gestureend', this.drop)
    this.canvas.$el.removeClass('is-dragging')

    if (this.active) {
      this.$el.trigger('end:dragging', this.controlWob)
    }

    var self = this
    this.snap.release()
    this.snap = null
  },

  release: function () {
    this.undelegateEvents()
  }

})

/*
 * By default, the draggable middleware will autoscroll the canvas.
 * You can override this by passing options `scrollX` and/or `scrollY`, setting
 * the flag to false.
 *
 * You can also override the autoscroll step with the property `scrollStep`, as well
 * as `scrollThreshold`
 */
export default function (options, canvas) {
  if (!canvas) {
    canvas = options
    options = {}
  }

  options = _.defaults(options, {
    scrollX: true,
    scrollY: true,
    scrollStep: 12,
    scrollThreshold: 30
  })

  return new Draggable({
    canvas: canvas,
    el: canvas.el,
    scroll: {
      x: options.scrollX,
      y: options.scrollY,
      step: options.scrollStep,
      threshold: options.scrollThreshold
    }
  })
}
